// @ts-check

// Vendor imports
const React = require('react')
// Client V2 common imports
const ToggleButton = require('client-v2/common/components/toggle-button')
const useClientEffect = require('client-v2/common/hooks/client-effect')
const tag = require('client-v2/common/utils/hyperscript')

// Constants

/**
 * @readonly
 * @enum {string}
 */
const Theme = {
  light: 'light',
  dark: 'dark'
}

/**
 * @readonly
 * @enum {string}
 */
const ThemeClass = {
  light: 'theme--light',
  dark: 'theme--dark'
}

function getThemeFromDocument() {
  const { classList } = document.documentElement
  if (classList.contains(ThemeClass.light)) {
    return Theme.light
  }
  if (classList.contains(ThemeClass.dark)) {
    return Theme.dark
  }
}

// React Contexts

/**
 * @typedef {Object} ThemeContext
 * @property {Theme} theme
 * @property {(theme: Theme) => void} setTheme
 */

/** @type {React.Context<ThemeContext>} */
const context = React.createContext({
  theme: undefined,
  setTheme: undefined
})

// React Components

/**
 * React Component that acts as the theme context provider
 * @param {Object} props
 * @param {React.ReactChildren} props.children
 * @returns {React.ReactElement}
 */
function ThemeManager(props) {
  const [ theme, setTheme ] = React.useState()
  useSetInitialTheme(setTheme)
  useSetThemeMediaQueryListener(setTheme)
  useThemeChanged(theme)
  return (
    tag(context.Provider, {
      value: { theme, setTheme }
    },
      props.children
    )
  )
}

/**
 * React Component that can toggle the theme
 * @param {Object} props
 * @param {(value: Theme) => void} [props.onChange]
 */
function ThemeToggleButton(props) {
  const { theme, setTheme } = React.useContext(context)
  const { onChange } = props
  const handleChange = React.useCallback(
    () => {
      const newTheme = (
        theme === Theme.light
        ? Theme.dark
        : Theme.light
      )
      setTheme(newTheme)
      if (onChange) {
        onChange(newTheme)
      }
    },
    [ theme, setTheme, onChange ]
  )
  return (
    tag(ToggleButton, {
      title: 'Toggle Theme',
      ...props,
      checked: theme === Theme.dark,
      onChange: handleChange
    })
  )
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')

  ThemeManager.propTypes = {
    children: PropTypes.node
  }

  ThemeToggleButton.propTypes = {
    onChange: PropTypes.func
  }
}

// React Hooks

/**
 * React Hook that inspects <html> to figure out the initial theme.
 * @param {React.Dispatch<React.SetStateAction<Theme>>} setTheme
 * @returns {void}
 * @see {@link https://github.com/mix/mix/blob/master/app/views/partials/scripts/theme.pug}
 */
function useSetInitialTheme(setTheme) {
  useClientEffect(
    () => {
      const initialTheme = getThemeFromDocument()
      if (initialTheme) {
        setTheme(initialTheme)
      }
    },
    [ setTheme ]
  )
}

/**
 * React Hook that listens for system/user-agent theme changes
 * @param {React.Dispatch<React.SetStateAction<Theme>>} setTheme
 * @returns {void}
 */
function useSetThemeMediaQueryListener(setTheme) {
  const listener = React.useCallback(
    event => { setTheme(event.matches ? Theme.dark : Theme.light) },
    [ setTheme ]
  )
  useClientEffect(
    () => {
      if (typeof matchMedia === 'function') {
        const mql = matchMedia('(prefers-color-scheme: dark)')
        mql.addListener(listener)
        return () => {
          mql.removeListener(listener)
        }
      }
    },
    [ listener ]
  )
}

/**
 * React Hook that updates <html> classes based on the current theme.
 * @param {Theme} theme
 * @returns {void}
 */
function useThemeChanged(theme) {
  useClientEffect(
    () => {
      const { classList } = document.documentElement
      if (theme === Theme.light) {
        classList.remove(ThemeClass.dark)
        classList.add(ThemeClass.light)
      }
      if (theme === Theme.dark) {
        classList.remove(ThemeClass.light)
        classList.add(ThemeClass.dark)
      }
    },
    [ theme ]
  )
}

/**
 * React Hook that gets the current theme from the context.
 * @returns {Theme}
 */
function useTheme() {
  const { theme } = React.useContext(context)
  return theme
}

// Exports

module.exports = {
  ThemeManager,
  ThemeToggleButton,
  useTheme,
  getThemeFromDocument
}
